// VendorModal.js
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";

const VendorModal = ({
  isOpen,
  onClose,
  vendorName,
  similarVendors,
  onCreateVendor,
  onSelectVendor,
  onResolve, // Called with the chosen vendor info after successful selection or creation
  pendingVendors = [], // Add these props for multi-vendor support
  currentVendorIndex = 0, // Add these props for multi-vendor support
}) => {
  const [name, setName] = useState(vendorName);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [option, setOption] = useState("create"); // 'create' or 'select'
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // When the modal opens, set the name from the vendorName prop.
    if (isOpen && vendorName) {
      setName(vendorName);
      // Reset submission state when a new vendor is shown
      setIsSubmitting(false);
    }
    // Reset state when modal closes.
    if (!isOpen) {
      setSelectedVendor(null);
      setOption("create");
      setIsSubmitting(false);
    }
  }, [isOpen, vendorName, currentVendorIndex]); // Add currentVendorIndex as a dependency

  const handleCancel = () => {
    setIsSubmitting(false);
    onClose(true); // Pass true to indicate user canceled
  };

  // In VendorModal.js, modify the handleSubmit function:
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let result;
      if (option === "create") {
        // Create a vendor using the provided handler.
        const response = await onCreateVendor(name);
        result = {
          name: name,
          id: response?.vendor?.Id,
        };
      } else if (option === "select") {
        // Use the selected vendor.
        result = selectedVendor;
      }

      if (result && result.name) {
        // Call onSelectVendor with the result
        // We'll keep isSubmitting true regardless of the outcome
        await onSelectVendor(result);

        // Add a small timeout to keep the button in submitting state
        // even if the parent component is slow to update the vendor index
        setTimeout(() => {
          // If we're still on the same vendor after a short delay,
          // and the modal is still open, force isSubmitting to true
          if (isOpen && vendorName === name) {
            setIsSubmitting(true);
          }
        }, 100);
      } else {
        throw new Error("Failed to process vendor selection");
      }
    } catch (error) {
      console.error("Vendor action failed:", error);
      // Only reset submitting state on error
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Determine if we're in a multi-vendor context
  const isMultiVendor = pendingVendors.length > 1;
  const vendorCountText = isMultiVendor
    ? `(${currentVendorIndex + 1} of ${pendingVendors.length})`
    : "";

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div
        className="absolute inset-0 bg-black opacity-50"
        onClick={() => !isSubmitting && onClose()}
      ></div>
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md z-10">
        <h3 className="text-xl font-semibold mb-3 text-gray-800">
          Vendor Issue {vendorCountText}
        </h3>
        <p className="text-gray-600 mb-4">
          The vendor <span className="font-medium">{vendorName}</span> was not
          found in QuickBooks. You may choose to create a new vendor with this
          name or select one from our suggestions.
        </p>

        <div className="mb-4">
          <div className="flex space-x-4 mb-3">
            <button
              type="button"
              className={`flex-1 py-2 px-4 border rounded-md ${
                option === "create"
                  ? "bg-blue-50 border-blue-500 text-blue-700"
                  : "border-gray-300 text-gray-700"
              }`}
              onClick={() => setOption("create")}
              disabled={isSubmitting}
            >
              Create New Vendor
            </button>
            {similarVendors?.length > 0 && (
              <button
                type="button"
                className={`flex-1 py-2 px-4 border rounded-md ${
                  option === "select"
                    ? "bg-blue-50 border-blue-500 text-blue-700"
                    : "border-gray-300 text-gray-700"
                }`}
                onClick={() => setOption("select")}
                disabled={isSubmitting}
              >
                Use Existing Vendor
              </button>
            )}
          </div>

          {option === "create" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendor Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={isSubmitting}
              />
            </div>
          )}

          {option === "select" && similarVendors?.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select a Vendor
              </label>
              <select
                value={selectedVendor?.id || ""}
                onChange={(e) => {
                  const selected = similarVendors.find(
                    (v) => v.id === e.target.value
                  );
                  setSelectedVendor(selected);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={isSubmitting}
              >
                <option value="">-- Select a vendor --</option>
                {similarVendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md shadow-sm disabled:opacity-50"
            disabled={isSubmitting || (option === "select" && !selectedVendor)}
          >
            {isSubmitting ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                {option === "create" ? "Creating..." : "Selecting..."}
              </>
            ) : (
              "Continue"
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default VendorModal;
